import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Fade,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PersonIcon from "@mui/icons-material/Person";
import autobotIcon from "../../assets/autobot-icon.svg";
import { useAppTheme } from "../../context/theme.context";
import { AuthContext } from "../../context/user.context";
import { formatMarkdown, getYouTubeEmbedUrl } from "./chatBotUtils";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../config/firebase-config";
import { isRouteAllowed } from "../../utils/routing/routePermissions";

const SUPPORT_WHATSAPP_URL =
  "https://wa.me/5493417548306?text=Hola,%20necesito%20ayuda%20con%20AutoApp";
const CHATBOT_TIMEOUT_MS = 25000;
const ROLE_BY_APP = {
  dealership_web: "dealership",
  group_web: "group",
  reseller_app: "reseller_web",
  developer_web: "developer",
};

const INTRO_BY_ROLE = {
  dealership: "la web de gestión de tu concesionaria",
  group: "el panel web de tu grupo",
  reseller_web: "la web de Reventas",
  developer: "el Panel de Desarrollador",
};

const buildChatbotScope = (currentUser) => ({
  requestedApp: currentUser?.activeApp || null,
  operatingUnitId:
    currentUser?.contextType === "operating_unit"
      ? currentUser?.activeUnitId || null
      : null,
  dealershipId: currentUser?.dealershipId || null,
  activeGroupId: currentUser?.activeGroupId || null,
  groupId: typeof currentUser?.groupId === "string" ? currentUser.groupId : null,
});

const ROUTES_BY_ROLE = {
  dealership: {
    dealership_home: "/",
    new_auction: "/new-auction",
    finished_auctions: "/auctions-finished",
    new_reseller: "/new-reseller",
    new_member: "/new-member",
    modify_dealership: "/modify-dealership",
    appraisal_config: "/appraisal-cfg",
    appraisals: "/appraisals",
    appraisal_close: "/appraisals-close",
    fast_purchase: "/fast-purchase",
    vehicle_analytics: "/vehicle-analytics",
    reseller_status: "/ban-reseller",
    members: "/members",
    units: "/units",
    loans: "/loans",
    cars_management: "/cars-management",
    test_drives: "/test-drives",
  },
  group: {
    group_home: "/",
    new_dealership: "/new-dealership",
    group_reseller_status: "/group-ban-reseller",
    group_admins: "/modify-admins",
    group_finished_auctions: "/auctions-finished",
    group_analytics: "/group-analytics",
    admin_reports: "/admin-reports",
    group_fast_purchase: "/fast-purchase",
  },
  reseller_web: {
    auctions: "/",
    favorites: "/my-favorites",
    my_offers: "/my-offers",
  },
  developer: {
    developer_home: "/",
    new_group: "/new-group",
    modify_group_members: "/modify-members",
    members_backfill: "/members-backfill",
    developer_vehicle_analytics: "/car-analytics",
    developer_cash_out: "/auctions-to-cash-out",
  },
};

const createSessionId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const ChatBot = () => {
  const { colors, isDark } = useAppTheme();
  const { currentUser } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [recommendedSuggestions, setRecommendedSuggestions] = useState([]);
  const [, setHasUnread] = useState(true);

  // El rol conversacional se deriva del contexto validado por backend.
  const userRole = ROLE_BY_APP[currentUser?.activeApp] || null;
  const chatbotScopeKey = [
    currentUser?.uid || "anonymous",
    currentUser?.activeApp || "no-app",
    currentUser?.activeUnitId || "no-unit",
    currentUser?.activeGroupId || "no-active-group",
    currentUser?.groupId || "no-group",
  ].join(":");

  const messagesEndRef = useRef(null);
  const requestInFlightRef = useRef(false);
  const activeRequestRef = useRef(null);
  const suggestionsLoadingRef = useRef(false);
  const sessionIdRef = useRef(null);
  const activeScopeKeyRef = useRef(chatbotScopeKey);
  const previousScopeKeyRef = useRef(chatbotScopeKey);

  activeScopeKeyRef.current = chatbotScopeKey;

  if (!sessionIdRef.current) {
    sessionIdRef.current = createSessionId();
  }

  // Nunca reutilizar una conversación al cambiar de app, usuario o unidad:
  // su historial también se envía al backend como contexto para responder.
  useEffect(() => {
    if (previousScopeKeyRef.current === chatbotScopeKey) return;

    previousScopeKeyRef.current = chatbotScopeKey;
    activeRequestRef.current = null;
    requestInFlightRef.current = false;
    suggestionsLoadingRef.current = false;
    sessionIdRef.current = createSessionId();
    setMessages([]);
    setRecommendedSuggestions([]);
    setInputText("");
    setIsTyping(false);
  }, [chatbotScopeKey]);

  useEffect(() => {
    if (
      !isOpen ||
      !userRole ||
      recommendedSuggestions.length > 0 ||
      suggestionsLoadingRef.current
    ) {
      return;
    }

    suggestionsLoadingRef.current = true;
    const requestScopeKey = chatbotScopeKey;
    const askAutobotFn = httpsCallable(functions, "askAutobotV2", {
      timeout: CHATBOT_TIMEOUT_MS,
    });

    askAutobotFn({
      ...buildChatbotScope(currentUser),
      message: "Hola",
      userRole,
      history: [],
      sessionId: sessionIdRef.current,
      context: {
        platform: "web",
        path: window.location.pathname,
        role: userRole,
      },
    })
      .then((result) => {
        if (activeScopeKeyRef.current !== requestScopeKey) return;
        const suggestions = result.data?.suggestions;
        if (result.data?.success && Array.isArray(suggestions)) {
          setRecommendedSuggestions(suggestions);
        }
      })
      .catch((error) => {
        console.warn("[AutoBot] No se pudieron cargar las sugerencias", {
          code: error?.code || "unknown",
        });
      })
      .finally(() => {
        if (activeScopeKeyRef.current === requestScopeKey) {
          suggestionsLoadingRef.current = false;
        }
      });
  }, [
    chatbotScopeKey,
    currentUser,
    isOpen,
    recommendedSuggestions.length,
    userRole,
  ]);

  const chatTitle = "AutoBot";

  const getChatContext = () => ({
    platform: "web",
    path: window.location.pathname,
    role: userRole,
  });

  const handleAction = (action) => {
    if (!action) return;

    if (action.type === "contact_support") {
      window.open(SUPPORT_WHATSAPP_URL, "_blank", "noopener,noreferrer");
      return;
    }

    if (action.type === "suggested_question" && action.question) {
      if (requestInFlightRef.current) return;
      handleSendMessage(action.question);
      return;
    }

    if (action.type === "open_screen") {
      const route = ROUTES_BY_ROLE[userRole]?.[action.target];
      if (route) {
        // GUARD-006: no navegar a destinos sin el permiso requerido.
        if (isRouteAllowed(route, currentUser)) {
          window.location.assign(route);
        }
      }
    }
  };

  // Inicializar conversación
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: "init-1",
          sender: "bot",
          text: `¡Hola! 👋 Soy AutoBot. Estoy acá para resolver tus dudas sobre ${INTRO_BY_ROLE[userRole] || "AutoApp"}.\n\nSeleccioná una de las dudas sugeridas o escribí tu consulta directamente en el chat.`,
          timestamp: new Date(),
          showSuggestions: true,
          videoUrl: null,
        },
      ]);
    }
  }, [messages, userRole]);

  // Auto scroll al final del chat
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  // Enviar mensaje del usuario y responder
  const handleSendMessage = async (textToSend) => {
    const text = textToSend || inputText;
    if (!userRole || !text.trim() || requestInFlightRef.current) return;

    const clientRequestId = `web-request-${Date.now()}`;
    const requestScopeKey = chatbotScopeKey;
    activeRequestRef.current = clientRequestId;
    requestInFlightRef.current = true;

    const userMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      text: text,
      timestamp: new Date(),
    };

    // Agregar el mensaje del usuario al chat inmediatamente
    setMessages((prev) => [...prev, userMessage]);
    if (!textToSend) setInputText("");

    setIsTyping(true);

    // Formatear el historial de la conversación actual
    const formattedHistory = messages
      .map((msg) => ({
        sender: msg.sender,
        text: msg.text,
      }))
      .filter((msg) => msg.text);

    const askAutobotFn = httpsCallable(functions, "askAutobotV2", {
      timeout: CHATBOT_TIMEOUT_MS,
    });

    try {
      const result = await askAutobotFn({
        ...buildChatbotScope(currentUser),
        message: text,
        userRole,
        history: formattedHistory,
        sessionId: sessionIdRef.current,
        context: getChatContext(),
      });

      if (
        activeRequestRef.current !== clientRequestId ||
        activeScopeKeyRef.current !== requestScopeKey
      ) return;

      const responseData = result.data;
      if (!responseData?.success || typeof responseData.text !== "string") {
        throw new Error("La función devolvió una respuesta inválida.");
      }

      if (responseData.degraded) {
        console.warn("[AutoBot] Respuesta degradada", {
          requestId: responseData.requestId || null,
          technicalError: !!responseData.technicalError,
        });
      }

      const responseSuggestions = Array.isArray(responseData.suggestions)
        ? responseData.suggestions
        : [];
      const botMsgId = `bot-${Date.now()}`;
      const newBotMsg = {
        id: botMsgId,
        sender: "bot",
        text: responseData.text,
        videoUrl: responseData.videoUrl || null,
        confidence: responseData.confidence ?? null,
        sourceQuestion: text,
        matchedQuestion: responseData.matchedQuestion || null,
        requestId: responseData.requestId || null,
        sessionId: sessionIdRef.current,
        actions: (responseData.actions || []).filter((action) => {
          if (action?.type !== "open_screen") return true;
          const route = ROUTES_BY_ROLE[userRole]?.[action.target];
          return Boolean(route && isRouteAllowed(route, currentUser));
        }),
        timestamp: new Date(),
        noMatch: !!responseData.noMatch,
        suggestionsAreRelated: !!responseData.suggestionsAreRelated,
        showSuggestions:
          !!responseData.showSuggestions || !!responseData.noMatch,
        suggestionsOverride:
          responseSuggestions.length > 0 ? responseSuggestions : null,
        serviceNotice: responseData.degraded
          ? responseData.technicalError
            ? "El asistente no está disponible temporalmente."
            : "Respuesta de contingencia: el asistente de IA no estuvo disponible."
          : null,
      };

      setMessages((prev) => {
        const updated = [...prev, newBotMsg];
        if (responseData.showVideo && responseData.videoUrl) {
          const embedUrl = getYouTubeEmbedUrl(responseData.videoUrl);
          if (embedUrl) {
            updated.push({
              id: `video-${Date.now()}`,
              sender: "bot",
              text: "",
              videoUrl: embedUrl,
              timestamp: new Date(),
              showSuggestions: false,
            });
          }
        }
        return updated;
      });
    } catch (error) {
      if (
        activeRequestRef.current !== clientRequestId ||
        activeScopeKeyRef.current !== requestScopeKey
      ) return;

      console.error("[AutoBot] Error al consultar el servicio", {
        code: error?.code || "unknown",
        message: error?.message || "Error desconocido",
      });
      const isTimeout =
        error?.code === "functions/deadline-exceeded" ||
        error?.code === "deadline-exceeded";
      setMessages((prev) => [
        ...prev,
        {
          id: `bot-error-${Date.now()}`,
          sender: "bot",
          text: isTimeout
            ? "La respuesta está demorando más de lo esperado. Intentá nuevamente en unos segundos."
            : "No pude conectarme con AutoBot en este momento. Intentá nuevamente o contactá a soporte.",
          timestamp: new Date(),
          showSuggestions: recommendedSuggestions.length > 0,
          suggestionsOverride: recommendedSuggestions,
          serviceNotice:
            "La consulta no fue respondida por la base de ayuda ni por la IA.",
          actions: [
            {
              type: "contact_support",
              label: "Contactar soporte",
            },
          ],
        },
      ]);
    } finally {
      if (
        activeRequestRef.current === clientRequestId &&
        activeScopeKeyRef.current === requestScopeKey
      ) {
        activeRequestRef.current = null;
        requestInFlightRef.current = false;
        setIsTyping(false);
      }
    }
  };

  // Reiniciar el chat
  const handleRestartChat = () => {
    activeRequestRef.current = null;
    requestInFlightRef.current = false;
    sessionIdRef.current = createSessionId();
    setMessages([
      {
        id: `restart-${Date.now()}-1`,
        sender: "bot",
        text: "Chat restablecido. ¡Hola de nuevo! 👋\n\nSelecciona una duda frecuente del listado o escribe tu consulta.",
        timestamp: new Date(),
        showSuggestions: true,
        videoUrl: null,
      },
    ]);
    setIsTyping(false);
    setInputText("");
  };

  return (
    <>
      {/* Botón Flotante (FAB) abajo a la derecha */}
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
        }}
      >
        <IconButton
          onClick={() => {
            setIsOpen(!isOpen);
            setHasUnread(false);
          }}
          sx={{
            bgcolor: colors.primary,
            color: "#ffffff",
            width: 56,
            height: 56,
            "&:hover": {
              bgcolor: colors.primaryDark || colors.primary,
              transform: "scale(1.05)",
            },
          }}
        >
          {isOpen ? <CloseIcon /> : <ChatIcon />}
        </IconButton>
      </Box>

      {/* Ventana de Chatbot abajo a la derecha */}
      <Fade in={isOpen} timeout={250}>
        <Paper
          elevation={16}
          sx={{
            position: "fixed",
            bottom: 92,
            right: 24,
            width: { xs: "calc(100vw - 32px)", sm: 380 },
            height: { xs: "calc(100vh - 120px)", sm: 550 },
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "16px",
            backgroundColor: colors.background.paper,
            border: `1px solid ${colors.border}`,
          }}
        >
          {/* Cabecera */}
          <Box
            sx={{
              bgcolor: colors.primary,
              color: "#ffffff",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  width: 40,
                  height: 40,
                  border: "1px solid rgba(255,255,255,0.4)",
                  "& img": { width: 22, height: 22 },
                }}
              >
                <img src={autobotIcon} alt="AutoBot" />
              </Avatar>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, fontSize: "0.9rem", lineHeight: 1.2 }}
                >
                  {chatTitle}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={handleRestartChat}
                title="Reiniciar chat"
                sx={{
                  color: "#ffffff",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                }}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                title="Cerrar"
                sx={{
                  color: "#ffffff",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Historial */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              bgcolor: isDark ? "#121212" : "#f8f9fa",
            }}
          >
            {messages.map((msg) => {
              const isBot = msg.sender === "bot";
              const messageSuggestions =
                Array.isArray(msg.suggestionsOverride) &&
                msg.suggestionsOverride.length > 0
                  ? msg.suggestionsOverride
                  : recommendedSuggestions;
              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignSelf: isBot ? "flex-start" : "flex-end",
                    maxWidth: "85%",
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      flexDirection: isBot ? "row" : "row-reverse",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: isBot ? colors.primary : colors.secondary,
                        "& img": { width: 16, height: 16 },
                      }}
                    >
                      {isBot ? (
                        <img src={autobotIcon} alt="AutoBot" />
                      ) : (
                        <PersonIcon sx={{ width: 16, height: 16 }} />
                      )}
                    </Avatar>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 1.5,
                        borderRadius: isBot
                          ? "0px 16px 16px 16px"
                          : "16px 0px 16px 16px",
                        backgroundColor: isBot
                          ? colors.background.paper
                          : colors.primary,
                        color: isBot ? colors.text.primary : "#ffffff",
                        border: isBot ? `1px solid ${colors.border}` : "none",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                        wordBreak: "break-word",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-line",
                          fontSize: "0.875rem",
                          lineHeight: 1.4,
                        }}
                      >
                        {formatMarkdown(msg.text)}
                      </Typography>
                      {msg.serviceNotice && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 1,
                            color: isDark ? "#ffcc80" : "#9a6700",
                            fontSize: "0.68rem",
                            lineHeight: 1.3,
                          }}
                        >
                          {msg.serviceNotice}
                        </Typography>
                      )}
                    </Paper>
                  </Box>

                  <Box
                    sx={{
                      alignSelf: isBot ? "flex-start" : "flex-end",
                      ml: isBot ? 5 : 0,
                      mr: isBot ? 0 : 5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                      maxWidth: "85%",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontSize: "0.7rem",
                        opacity: 0.8,
                      }}
                    >
                      {msg.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>

                  {/* Video explicativo */}
                  {isBot && msg.videoUrl && !msg.text && (
                    <Box sx={{ mt: 1, ml: 5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontWeight: 500,
                          mb: 0.5,
                          display: "block",
                        }}
                      >
                        📹 Video explicativo:
                      </Typography>
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          maxWidth: 280,
                          aspectRatio: "16/9",
                          borderRadius: 2,
                          overflow: "hidden",
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <iframe
                          src={msg.videoUrl}
                          title="Video explicativo"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Sugerencias provistas por askAutobotV2 */}
                  {isBot &&
                    msg.showSuggestions &&
                    messageSuggestions.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          ml: 5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.8,
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        >
                          {msg.noMatch && msg.suggestionsAreRelated
                            ? "Quizás te sirvan estas preguntas relacionadas:"
                            : msg.noMatch
                              ? "También podés consultar estas preguntas frecuentes:"
                              : "Preguntas recomendadas:"}
                        </Typography>
                        {messageSuggestions.map((item) => (
                          <Button
                            key={item.id}
                            variant="outlined"
                            size="small"
                            disabled={isTyping}
                            onClick={() => handleSendMessage(item.question)}
                            sx={{
                              textAlign: "left",
                              justifyContent: "flex-start",
                              textTransform: "none",
                              fontSize: "0.8rem",
                              borderRadius: "20px",
                              borderColor: colors.primary,
                              color: colors.primary,
                              py: 0.5,
                              px: 1.5,
                              lineHeight: 1.2,
                              width: "fit-content",
                              maxWidth: "100%",
                              "&:hover": {
                                bgcolor: isDark
                                  ? "rgba(0,180,216,0.08)"
                                  : "rgba(0,124,162,0.05)",
                                borderColor: colors.primary,
                              },
                            }}
                          >
                            {item.question}
                          </Button>
                        ))}
                      </Box>
                    )}

                  {/* Acciones sugeridas por el backend */}
                  {isBot &&
                    Array.isArray(msg.actions) &&
                    msg.actions.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          ml: 5,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.8,
                          alignItems: "flex-start",
                        }}
                      >
                        {msg.actions.map((action, index) => (
                          <Button
                            key={`${action.type}-${action.target || index}`}
                            variant="contained"
                            size="small"
                            disabled={isTyping}
                            onClick={() => handleAction(action)}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.8rem",
                              borderRadius: "20px",
                              bgcolor: colors.primary,
                              color: "#ffffff",
                              py: 0.5,
                              px: 1.5,
                              lineHeight: 1.2,
                              "&:hover": {
                                bgcolor: colors.primaryDark || colors.primary,
                              },
                            }}
                          >
                            {action.label}
                          </Button>
                        ))}
                      </Box>
                    )}
                </Box>
              );
            })}

            {/* Escribiendo... */}
            {isTyping && (
              <Box
                sx={{
                  display: "flex",
                  alignSelf: "flex-start",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: colors.primary,
                    "& img": { width: 16, height: 16 },
                  }}
                >
                  <img src={autobotIcon} alt="AutoBot" />
                </Avatar>
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    borderRadius: "0px 16px 16px 16px",
                    backgroundColor: colors.background.paper,
                    border: `1px solid ${colors.border}`,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    height: 32,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.6,
                      "& span": {
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: colors.primary,
                        display: "inline-block",
                        animation: "bounce 1.4s infinite ease-in-out both",
                      },
                      "& span:nth-of-type(1)": { animationDelay: "-0.32s" },
                      "& span:nth-of-type(2)": { animationDelay: "-0.16s" },
                      "@keyframes bounce": {
                        "0%, 80%, 100%": { transform: "scale(0)" },
                        "40%": { transform: "scale(1)" },
                      },
                    }}
                  >
                    <span />
                    <span />
                    <span />
                  </Box>
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Formulario */}
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            sx={{
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderTop: `1px solid ${colors.border}`,
              backgroundColor: colors.background.paper,
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Escribe tu consulta..."
              autoComplete="off"
              disabled={isTyping}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "24px",
                  backgroundColor: isDark ? "#1e1e1e" : "#f1f5f9",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: colors.border },
                  "&.Mui-focused fieldset": { borderColor: colors.primary },
                },
                "& .MuiInputBase-input": { fontSize: "0.875rem", py: 1 },
              }}
            />
            <IconButton
              type="submit"
              color="primary"
              disabled={!inputText.trim() || isTyping}
              sx={{
                bgcolor: colors.primary,
                color: "#ffffff",
                width: 36,
                height: 36,
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: colors.primaryDark || colors.primaryHover,
                  transform: "scale(1.05)",
                },
                "&.Mui-disabled": {
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)",
                  color: colors.disabled,
                },
              }}
            >
              <SendIcon sx={{ fontSize: 16, ml: 0.3 }} />
            </IconButton>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default ChatBot;
